import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { fmtL, fmtDate, fmtDateTime, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { PrinterIcon, DownloadSimpleIcon, ReceiptIcon, WarningIcon } from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";
import { withTemplate } from "@/components/TemplatePicker";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Vista de impresión del RECIBO OFICIAL (PDF) dentro de la aplicación.
 * Solo CONSULTA el recibo ya guardado: imprimirlo, reimprimirlo o cerrar esta ventana no registra ni duplica cobros.
 */
export function ReciboImpresionModal({ pagoId, numero, onClose }) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState("");
  const frame = useRef(null);
  const vm = useModalForm({ onClose });
  useEffect(() => {
    let u = null;
    api.get(withTemplate(`/payments/${pagoId}/receipt-pdf`, "recibo"), { responseType: "blob" })
      .then((res) => { u = URL.createObjectURL(res.data); setUrl(u); })
      .catch((e) => setError(apiErr(e)));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [pagoId]);
  const imprimir = () => {
    try { frame.current?.contentWindow?.focus(); frame.current?.contentWindow?.print(); }
    catch { window.open(url, "_blank"); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...vm.overlayProps}>
      <div className="bg-card border border-agro-green/40 rounded-lg w-full max-w-4xl h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}
        data-testid="recibo-impresion">
        <div className="p-4 border-b border-border flex items-center gap-3">
          <ReceiptIcon size={22} className="text-agro-green" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wider text-agro-green">Recibo oficial de pago</div>
            <div className="font-heading font-semibold font-mono" data-testid="recibo-impresion-numero">{numero || "—"}</div>
          </div>
          <button onClick={imprimir} disabled={!url} data-testid="recibo-imprimir"
            className="px-4 py-2 text-xs bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md flex items-center gap-1.5 disabled:opacity-50">
            <PrinterIcon size={14} weight="bold" /> IMPRIMIR
          </button>
          {url && (
            <a href={url} download={`recibo-${numero || pagoId.slice(0, 8)}.pdf`} data-testid="recibo-descargar"
              className="px-3 py-2 text-xs border border-border rounded-md flex items-center gap-1.5 text-text-secondary hover:bg-white/5">
              <DownloadSimpleIcon size={14} /> PDF
            </a>
          )}
          <BotonCerrar onClick={vm.cerrar} />
        </div>
        <div className="flex-1 bg-app">
          {error ? <div className="p-8 text-center text-red-400 text-sm">{error}</div>
            : !url ? <div className="p-8 text-center text-text-muted text-sm">Generando el recibo…</div>
            : <iframe ref={frame} src={url} title="Recibo oficial" className="w-full h-full" data-testid="recibo-pdf" />}
        </div>
      </div>
    </div>
  );
}

/** Detalle de un recibo desde un movimiento de la cuenta: datos del cobro y su aplicación a cada factura. */
export function ReciboDetalleModal({ pagoId, onClose }) {
  const [p, setP] = useState(null);
  const [imprimir, setImprimir] = useState(false);
  const vm = useModalForm({ onClose });
  useEffect(() => {
    api.get(`/payments/${pagoId}`).then(({ data }) => setP(data)).catch((e) => { toast.error(apiErr(e)); onClose(); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagoId]);
  const anulado = p?.estado === "anulado";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...vm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}
        data-testid="recibo-detalle">
        <div className="p-5 border-b border-border flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-green">Recibo oficial</div>
            <h3 className="font-heading text-lg font-mono">{p?.numero_recibo || "…"}
              {anulado && <span className="ml-2 text-[10px] px-2 py-0.5 rounded border border-red-500/40 text-red-400 align-middle">ANULADO</span>}
            </h3>
            {p && <div className="text-xs text-text-muted">{p.cliente_codigo ? `${p.cliente_codigo} · ` : ""}{p.cliente_nombre}</div>}
          </div>
          <BotonCerrar onClick={vm.cerrar} />
        </div>
        {!p ? <div className="p-8 text-center text-text-muted text-sm">Cargando…</div> : (
          <div className="p-5 space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <Dato l="Fecha del pago" v={fmtDate(p.fecha)} />
              <Dato l="Importe recibido" v={fmtL(p.monto_total ?? p.monto)} />
              <Dato l="Aplicado a documentos" v={fmtL(p.monto_aplicado ?? p.monto)} />
              <Dato l="Saldo a favor generado" v={fmtL(p.monto_excedente || 0)} />
              <Dato l="Método" v={`${(p.metodo || "").replace("_", " ")}${p.referencia ? ` · ${p.referencia}` : ""}${p.banco ? ` · ${p.banco}` : ""}`} />
              <Dato l="Registró el cobro" v={`${p.usuario_nombre || p.created_by || "—"}${p.created_at ? ` · ${fmtDateTime(p.created_at)}` : ""}`} />
              {anulado && <Dato l="Anulado" v={`${p.anulado_por_nombre || p.anulado_por || ""} · ${fmtDateTime(p.anulado_at)} · ${p.motivo_anulacion || ""}`} />}
            </div>
            <div className="border border-border rounded-md overflow-hidden">
              <div className="px-3 py-2 bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">Aplicación a facturas</div>
              <table className="w-full text-xs" data-testid="recibo-aplicaciones">
                <thead className="text-text-muted text-[10px] uppercase"><tr className="text-left">
                  <th className="p-2">Documento</th><th className="p-2 text-right">Saldo anterior</th>
                  <th className="p-2 text-right">Aplicado</th><th className="p-2 text-right">Saldo posterior</th>
                </tr></thead>
                <tbody>
                  {(p.aplicaciones || []).map((a, i) => (
                    <tr key={i} className="border-t border-border/40">
                      <td className="p-2 font-mono">{a.factura_numero}
                        {(() => {
                          const al = (p.allocations || []).find((x) => x.factura_id === a.factura_id);
                          return al?.subcuenta_codigo ? <div className="text-[10px] text-text-muted font-sans">{al.subcuenta_codigo} {al.proyecto_nombre || ""}</div> : null;
                        })()}
                      </td>
                      <td className="p-2 text-right">{a.saldo_anterior != null ? fmtL(a.saldo_anterior) : "—"}</td>
                      <td className="p-2 text-right text-agro-green">{fmtL(a.monto)}</td>
                      <td className="p-2 text-right">{a.saldo_posterior != null ? fmtL(a.saldo_posterior) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {p.cuenta_destino?.nombre && (
              <div className="text-xs text-text-secondary" data-testid="recibo-destino">
                Destino del dinero: <b>{p.cuenta_destino.nombre}</b>{p.cuenta_destino.banco ? ` · ${p.cuenta_destino.banco}` : ""}
              </div>
            )}
            <ComisionesPago pagoId={p.id} anulado={anulado} />
            <div className="flex justify-end">
              <button onClick={() => setImprimir(true)} data-testid="recibo-detalle-imprimir"
                className="px-4 py-2 text-xs bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md flex items-center gap-1.5">
                <PrinterIcon size={14} weight="bold" /> VER / IMPRIMIR RECIBO
              </button>
            </div>
          </div>
        )}
      </div>
      {imprimir && p && <ReciboImpresionModal pagoId={p.id} numero={p.numero_recibo} onClose={() => setImprimir(false)} />}
    </div>
  );
}

function Dato({ l, v }) {
  return (
    <div className="bg-app border border-border rounded p-2">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{l}</div>
      <div className="text-text-primary mt-0.5">{v}</div>
    </div>
  );
}


/** GRATIVA (Gratificación Variable) generada por el cobro (dato interno; solo roles con acceso a GRATIVA). */
function ComisionesPago({ pagoId, anulado }) {
  const { user } = useAuth();
  const ver = user?.role === "admin" || Boolean(user?.effective?.is_admin) || Boolean(user?.effective?.flags?.ver_comisiones);
  const esAdmin = user?.role === "admin" || Boolean(user?.effective?.is_admin);
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!ver) return;
    api.get(`/payments/${pagoId}/comisiones`).then(({ data }) => setC(data)).catch(() => setC(null));
  }, [pagoId, ver]);
  if (!ver || !c || (c.estado === "no_aplica" && !c.filas.length)) return null;
  const recalcular = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/payments/${pagoId}/comisiones/recalcular`); setC(data); toast.success("GRATIVA recalculada"); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-border rounded-md overflow-hidden" data-testid="recibo-comisiones">
      <div className="px-3 py-2 bg-sidebar text-[10px] uppercase tracking-wider text-text-muted flex items-center justify-between">
        <span>GRATIVA del cobro · Gratificación Variable (interno)</span>
        <span className={c.estado === "pendiente" ? "text-agro-yellow" : c.estado === "anulada" ? "text-red-400" : "text-agro-green"}>{c.estado}</span>
      </div>
      {c.filas.length > 0 && (
        <table className="w-full text-xs">
          <thead className="text-text-muted text-[10px] uppercase"><tr className="text-left">
            <th className="p-2">Factura · RTC</th><th className="p-2">Proveedor · nivel</th><th className="p-2 text-right">GRATIVA base</th>
            <th className="p-2 text-right">Deducción</th><th className="p-2 text-right">GRATIVA ajustada</th><th className="p-2">Estado</th>
          </tr></thead>
          <tbody>
            {c.filas.map((f, i) => (
              <tr key={i} className="border-t border-border/40">
                <td className="p-2 font-mono">{f.factura_numero}<div className="font-sans text-[10px] text-text-muted">{f.rtc_nombre}</div></td>
                <td className="p-2">{f.proveedor} · {f.categoria} {f.nivel} · {f.porcentaje}%
                  <div className="text-[10px] text-text-muted">{f.dias_atraso < 0 ? "Contado" : `${f.dias_atraso} días`} · {f.tramo} (×{f.factor})</div></td>
                <td className="p-2 text-right">{fmtL(f.base)}</td>
                <td className="p-2 text-right text-red-400">{f.deduccion ? `− ${fmtL(f.deduccion)}` : "—"}</td>
                <td className="p-2 text-right text-agro-green">{fmtL(f.ajustada)}</td>
                <td className="p-2">{f.estado}{f.pagado > 0 ? ` · pagado ${fmtL(f.pagado)}` : ""}</td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr className="border-t border-border font-semibold">
            <td className="p-2" colSpan="2">Totales</td>
            <td className="p-2 text-right">{fmtL(c.totales.base)}</td>
            <td className="p-2 text-right text-red-400">− {fmtL(c.totales.deduccion)}</td>
            <td className="p-2 text-right text-agro-green">{fmtL(c.totales.ajustada)}</td>
            <td className="p-2">GRATIVA por pagar {fmtL(c.totales.por_pagar)}</td>
          </tr></tfoot>
        </table>
      )}
      {c.pendientes.length > 0 && (
        <div className="p-2 text-xs text-agro-yellow space-y-0.5" data-testid="recibo-comisiones-pendientes">
          {c.pendientes.map((x, i) => <div key={i}><WarningIcon size={12} className="inline align-[-2px]" aria-hidden="true" /> {x.factura_numero}: {x.detalle}</div>)}
          {esAdmin && !anulado && (
            <button onClick={recalcular} disabled={busy} data-testid="recalcular-comisiones"
              className="mt-1 px-3 py-1 border border-agro-yellow/60 rounded text-agro-yellow disabled:opacity-50">
              {busy ? "Recalculando…" : "Recalcular GRATIVA pendiente"}</button>
          )}
        </div>
      )}
    </div>
  );
}
